import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, onSnapshot, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../config/firebase';
import { FIRESTORE_PATHS } from '../config/appConfig';
import { useUser } from '../contexts/UserContext';
import { Calendar, TrendingUp, Target as TargetIcon, Activity, BarChart3, Home, Camera, Users } from 'lucide-react';
import TabButton from '../components/TabButton';
import pako from 'pako';
import logger from '../utils/logger';

// Import Chart.js components
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// Helper function to parse dates from session IDs
const parseDateFromSessionId = (id) => {
    if (!id || typeof id !== 'string') return null;

    const numericIdMatch = id.match(/\d{13,}/);
    if (numericIdMatch) {
        const timestamp = parseInt(numericIdMatch[0], 10);
        const date = new Date(timestamp);
        if (!isNaN(date.getTime()) && date.getFullYear() > 2000) {
            return date;
        }
    }

    let match = id.match(/(\d{4})(\d{2})(\d{2})[_\-]?(\d{2})?(\d{2})?(\d{2})?/);
    if (match) {
        const year = match[1];
        const month = match[2];
        const day = match[3];
        const hour = match[4] || '00';
        const minute = match[5] || '00';
        const second = match[6] || '00';

        const dateString = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
        const date = new Date(dateString);
        
        if (!isNaN(date.getTime()) && date.getFullYear() > 2000) {
            return date;
        }
    }

    return null;
};

// Helper function for timestamp formatting
const formatTimestamp = (timestamp, sessionId) => {
    let milliseconds = null;

    if (timestamp) {
        if (typeof timestamp.toDate === 'function') {
            milliseconds = timestamp.toDate().getTime();
        }
        else if (timestamp._seconds !== undefined) {
            milliseconds = timestamp._seconds * 1000;
        }
        else if (typeof timestamp === 'number') {
            milliseconds = (timestamp < 1000000000000) ? timestamp * 1000 : timestamp;
        } 
        else {
            let date = new Date(timestamp);
            if (!isNaN(date.getTime())) {
                milliseconds = date.getTime();
            }
        }
    }

    if (milliseconds !== null && milliseconds < 3600000) { 
        milliseconds = null; 
    }

    if (milliseconds === null && sessionId) {
        const dateFromId = parseDateFromSessionId(sessionId);
        if (dateFromId) {
            milliseconds = dateFromId.getTime();
        }
    }

    if (milliseconds === null || isNaN(new Date(milliseconds).getTime())) {
        return 'N/A';
    }

    const date = new Date(milliseconds);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper function to get session time in milliseconds
const getSessionTimeMs = (session) => {
    const timestamp = session.startTimeStamp; 
    const sessionId = session.id;
    
    if (timestamp) {
        if (typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
        if (timestamp._seconds !== undefined) return timestamp._seconds * 1000;
        if (typeof timestamp === 'number') return (timestamp < 1000000000000) ? timestamp * 1000 : timestamp;
        let date = new Date(timestamp);
        if (!isNaN(date.getTime())) return date.getTime();
    }

    if (sessionId) {
         const dateFromId = parseDateFromSessionId(sessionId);
         return dateFromId ? dateFromId.getTime() : 0;
    }
    return 0; 
};

function PlayerProfile() { 
    const { user } = useUser();
    const navigate = useNavigate();
    
    // State definitions
    const [allSessions, setAllSessions] = useState([]); 
    const [visits, setVisits] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null); 
    const [sessionDetails, setSessionDetails] = useState({}); 
    const [allUserEvents, setAllUserEvents] = useState([]); 
    const [mostPlayedMode, setMostPlayedMode] = useState('N/A');
    
    // Filter State
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    
    // Chart State
    const [chartPeriod, setChartPeriod] = useState('Daily'); 
    const [chartData, setChartData] = useState({ 
        labels: [], scores: [], accuracies: [], strikeRates: [], sessionCounts: [] 
    });
    const [chartLoading, setChartLoading] = useState(false);

    // Ball Stats state
    const [ballStats, setBallStats] = useState({
        TotalMiss: 0, TotalBallsPlayed: 0, TotalSingle: 0, TotalDouble: 0, 
        TotalTriple: 0, TotalFours: 0, TotalSixes: 0, TotalOut: 0,
    });
    
    // Initial Data Fetching
    useEffect(() => {
        if (!db || !user) {
            setLoading(false);
            return;
        }
        
        setLoading(false); // Show page immediately
        setError(null);
        
        const userId = user.uid;

        const visitsCollectionRef = collection(db, FIRESTORE_PATHS.USER_ANALYTICS, userId, FIRESTORE_PATHS.ANALYTICS_DATA.VISITS);
        const unsubscribeVisits = onSnapshot(query(visitsCollectionRef), (snapshot) => {
            const visitsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setVisits(visitsList);
        }, (err) => {
            logger.error('PlayerProfile', 'Error fetching visits:', err);
            setError(`Unable to fetch data. Please check your connection.`);
        });

        const sessionsCollectionRef = collection(db, FIRESTORE_PATHS.USERS, userId, FIRESTORE_PATHS.VR_DATA.SESSIONS);
        const unsubscribeSessions = onSnapshot(query(sessionsCollectionRef), (snapshot) => {
            const sessionsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllSessions(sessionsList);
        }, (err) => {
            logger.error('PlayerProfile', 'Error fetching sessions:', err);
            setError(`Unable to fetch data. Please check your connection.`);
        });

        return () => {
            unsubscribeVisits();
            unsubscribeSessions();
        };
    }, [db, user]);

    // Fetch all events
    useEffect(() => {
        if (db && user && visits.length > 0) {
            const fetchAllEvents = async () => {
                const userId = user.uid;
                let events = [];
                for (const visit of visits) {
                    const eventsRef = collection(db, FIRESTORE_PATHS.USER_ANALYTICS, userId, FIRESTORE_PATHS.ANALYTICS_DATA.VISITS, visit.id, FIRESTORE_PATHS.ANALYTICS_DATA.EVENTS);
                    const eventsSnap = await getDocs(eventsRef);
                    events = [...events, ...eventsSnap.docs.map(doc => ({ ...doc.data(), visitId: visit.id }))];
                }
                setAllUserEvents(events);
            };
            fetchAllEvents();
        }
    }, [db, user, visits]);

    // Sorted Sessions
    const sortedAllSessions = useMemo(() => {
        const sorted = [...allSessions];
        sorted.sort((a, b) => getSessionTimeMs(b) - getSessionTimeMs(a));
        return sorted;
    }, [allSessions]);

    // Filtered Sessions
    const filteredSessionsForStats = useMemo(() => {
        let filtered = [...allSessions];

        let startMs = 0;
        if (startDateFilter) {
            startMs = new Date(startDateFilter).setHours(0, 0, 0, 0);
        }

        let endMs = Infinity;
        if (endDateFilter) {
            endMs = new Date(endDateFilter).setHours(23, 59, 59, 999);
        }

        if (startMs > 0 || endMs !== Infinity) {
            filtered = filtered.filter(session => {
                const sessionTime = getSessionTimeMs(session);
                return sessionTime >= startMs && sessionTime <= endMs;
            });
        }
        
        return filtered;
    }, [allSessions, startDateFilter, endDateFilter]);

    // Filtered Events
    const filteredEvents = useMemo(() => {
        let startMs = 0;
        if (startDateFilter) {
            startMs = new Date(startDateFilter).setHours(0, 0, 0, 0);
        }

        let endMs = Infinity;
        if (endDateFilter) {
            endMs = new Date(endDateFilter).setHours(23, 59, 59, 999);
        }

        return allUserEvents.filter(event => {
            const eventTime = event.Timestamp ? event.Timestamp.toDate().getTime() : 0;
            return eventTime >= startMs && eventTime <= endMs;
        });
    }, [allUserEvents, startDateFilter, endDateFilter]);

    // Ball Stats Calculation
    useEffect(() => {
        const calculateBallStatsFromEvents = () => {
            let totalBallsPlayed = 0, totalMiss = 0, totalSingle = 0, totalDouble = 0, 
                totalTriple = 0, totalFours = 0, totalSixes = 0, totalOut = 0;
            let scoredCount = 0;

            filteredEvents.forEach(event => {
                const name = (event.eventName || event.EventName || event.eventType || '').trim();
                if (name.startsWith('(Next Ball) :')) {
                    totalBallsPlayed++;
                } else if (name.startsWith('Scores on Current Ball:')) {
                    const scoreStr = name.split(': ')[1]?.trim();
                    const score = parseInt(scoreStr, 10);
                    if (!isNaN(score)) {
                        scoredCount++;
                        if (score === 1) totalSingle++;
                        else if (score === 2) totalDouble++;
                        else if (score === 3) totalTriple++;
                        else if (score === 4) totalFours++;
                        else if (score === 6) totalSixes++;
                    }
                } else if (name === 'Wicket!') {
                    totalOut++;
                }
            });

            totalMiss = totalBallsPlayed - scoredCount;

            setBallStats({
                TotalMiss: totalMiss, TotalBallsPlayed: totalBallsPlayed, TotalSingle: totalSingle,
                TotalDouble: totalDouble, TotalTriple: totalTriple, TotalFours: totalFours,
                TotalSixes: totalSixes, TotalOut: totalOut
            });
        };

        calculateBallStatsFromEvents();
    }, [filteredEvents]);

    // Most Played Mode Calculation
    useEffect(() => {
        const calculateMostPlayedMode = () => {
            const modeFrequency = {};
            filteredEvents.forEach(event => {
                const name = (event.eventName || event.EventName || event.eventType || '').trim();
                if (name.startsWith('Game Manager Mode Started :')) {
                    const mode = name.split(': ')[1]?.trim();
                    if (mode) {
                        modeFrequency[mode] = (modeFrequency[mode] || 0) + 1;
                    }
                }
            });

            if (Object.keys(modeFrequency).length > 0) {
                const mostPlayed = Object.entries(modeFrequency).reduce((a, b) => a[1] > b[1] ? a : b)[0];
                setMostPlayedMode(mostPlayed);
            } else {
                setMostPlayedMode('N/A');
            }
        };

        calculateMostPlayedMode();
    }, [filteredEvents]);

    // Fetch Session Details
    // Priority: Use session.summary (VRCricket writes this after refactoring)
    // Fallback: Decompress delivery blobs for pre-refactoring sessions
    const fetchSessionDetails = useCallback(async (session) => {
        const sessionId = session.id;
        setSessionDetails((prev) => ({ ...prev, [sessionId]: { loading: true, error: null } }));

        try {
            // Check if session has a summary (VRCricket SessionSummary pattern)
            if (session.summary) {
                const s = session.summary;
                const totalBallsPlayed = s.totalBalls || s.totalBallsPlayed || 0;
                const totalRuns = s.totalRuns || 0;
                const totalMiss = s.totalMisses || 0;
                const totalFour = s.totalFours || 0;
                const totalSixes = s.totalSixes || 0;
                const totalOut = s.totalOuts || 0;
                const sessionStrikeRate = s.avgStrikeRate || (totalBallsPlayed > 0 ? ((totalRuns / totalBallsPlayed) * 100).toFixed(2) : '0.00');
                const sessionAccuracy = s.avgAccuracy || (totalBallsPlayed > 0 ? (((totalBallsPlayed - totalMiss) / totalBallsPlayed) * 100).toFixed(2) : '0.00');

                setSessionDetails((prev) => ({
                    ...prev,
                    [sessionId]: {
                        totalRuns, totalBallsPlayed, totalMiss,
                        totalFour, totalSixes, totalOut,
                        strikeRate: sessionStrikeRate, accuracy: sessionAccuracy,
                        loading: false, error: null
                    },
                }));
                return;
            }

            // Fallback: Decompress delivery blobs (pre-refactoring sessions without summary)
            let totalRuns = 0;
            let totalBallsPlayed = 0;
            let totalMiss = 0;
            let totalFour = 0;
            let totalSixes = 0;
            let totalOut = 0;

            const deliveriesCollectionRef = collection(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.VR_DATA.SESSIONS, sessionId, FIRESTORE_PATHS.VR_DATA.DELIVERIES);
            const deliveriesSnapshot = await getDocs(query(deliveriesCollectionRef));

            deliveriesSnapshot.forEach((doc) => {
                const data = doc.data();
                if (!data.ballCompletionResults) return;

                let decompressedData = null;
                try {
                    const compressed = atob(data.ballCompletionResults);
                    const charData = compressed.split('').map((c) => c.charCodeAt(0));
                    const binData = new Uint8Array(charData);
                    const strData = pako.inflate(binData, { to: 'string' });
                    decompressedData = JSON.parse(strData);
                } catch (err) {
                    return;
                }

                const result = decompressedData.scoreOnCurrentBall ?? 0;
                const outResult = decompressedData.wicketOnCurrentBall === true;

                totalBallsPlayed += 1;
                totalRuns += result;

                if (result === 0) totalMiss++;
                else if (result === 4) totalFour++;
                else if (result === 6) totalSixes++;
                if (outResult) totalOut++;
            });

            const sessionStrikeRate = totalBallsPlayed > 0 ? ((totalRuns / totalBallsPlayed) * 100).toFixed(2) : '0.00';
            const sessionAccuracy = totalBallsPlayed > 0 ? (((totalBallsPlayed - totalMiss) / totalBallsPlayed) * 100).toFixed(2) : '0.00';

            setSessionDetails((prev) => ({
                ...prev,
                [sessionId]: {
                    totalRuns, totalBallsPlayed, totalMiss,
                    totalFour, totalSixes, totalOut,
                    strikeRate: sessionStrikeRate, accuracy: sessionAccuracy,
                    loading: false, error: null
                },
            }));
        } catch (err) {
            setSessionDetails((prev) => ({
                ...prev,
                [sessionId]: { loading: false, error: err.message },
            }));
        }
    }, [db, user]);
    
    useEffect(() => {
        if (selectedSession && !sessionDetails[selectedSession.id]) {
            fetchSessionDetails(selectedSession);
        }
    }, [selectedSession, fetchSessionDetails, sessionDetails]);

    // Derived Metrics
    const totalRuns = useMemo(() => 
        ballStats.TotalSingle*1 + ballStats.TotalDouble*2 + ballStats.TotalTriple*3 + ballStats.TotalFours*4 + ballStats.TotalSixes*6,
        [ballStats]
    );
    const strikeRate = useMemo(() => 
        ballStats.TotalBallsPlayed > 0 ? ((totalRuns / ballStats.TotalBallsPlayed) * 100).toFixed(2) : '0.00',
        [totalRuns, ballStats.TotalBallsPlayed]
    );
    const accuracy = useMemo(() => 
        ballStats.TotalBallsPlayed > 0 ? (((ballStats.TotalBallsPlayed - ballStats.TotalMiss) / ballStats.TotalBallsPlayed) * 100).toFixed(2) : '0.00',
        [ballStats.TotalBallsPlayed, ballStats.TotalMiss]
    );

    // Handle Session Click
    const handleSessionClick = (session) => {
        setSelectedSession(selectedSession?.id === session.id ? null : session);
    };

    // Chart configurations
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: { 
                beginAtZero: true, 
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: { color: '#9ca3af' }
            },
            x: { 
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: { color: '#9ca3af' }
            },
        },
        plugins: {
            legend: { display: false },
            title: { 
                display: true, 
                text: 'Performance Over Time', 
                color: '#ffffff',
                font: { size: 16, weight: 'bold' }
            },
        },
    };

    const performanceChartData = {
        labels: chartData.labels,
        datasets: [{
            label: 'Total Runs',
            data: chartData.scores,
            borderColor: 'rgba(34, 197, 94, 1)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: true,
            tension: 0.4,
        }],
    };

    // Data loading states
    const isDataLoading = allSessions.length === 0 && visits.length === 0 && !error;
    const hasData = allSessions.length > 0 || visits.length > 0;

    return (
        <div className="h-screen w-full bg-gray-900 text-gray-100 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-br from-gray-900 via-gray-900 to-black p-6 border-b border-gray-800">
                <div className="flex items-center justify-center mb-4">
                    <h1 className="text-2xl font-bold text-white">Cricket X Profile</h1>
                </div>
            </div>

            <div className="p-4 space-y-6">
                {/* Error Message */}
                {error && (
                    <div className="bg-red-900/20 border border-red-500/50 p-4 rounded-2xl">
                        <p className="text-red-400 text-sm">{error}</p>
                    </div>
                )}

                {/* Loading Indicator */}
                {isDataLoading && (
                    <div className="bg-green-900/20 border border-green-500/50 p-4 rounded-2xl flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green-500 mr-3"></div>
                        <p className="text-green-400 text-sm font-semibold">Fetching your cricket data...</p>
                    </div>
                )}

                {/* Date Filters */}
                <div className="bg-gray-800 p-4 rounded-2xl border border-gray-700">
                    <h3 className="text-white font-bold mb-3 flex items-center">
                        <Calendar size={18} className="mr-2 text-green-400" />
                        Filter by Date
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="date"
                            value={startDateFilter}
                            onChange={(e) => setStartDateFilter(e.target.value)}
                            className="bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none flex-1"
                        />
                        <input
                            type="date"
                            value={endDateFilter}
                            onChange={(e) => setEndDateFilter(e.target.value)}
                            className="bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none flex-1"
                        />
                        <button
                            onClick={() => { setStartDateFilter(''); setEndDateFilter(''); }}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard title="Total Runs" value={totalRuns} icon={TargetIcon} color="green" />
                    <StatCard title="Balls Faced" value={ballStats.TotalBallsPlayed} icon={Activity} color="yellow" />
                    <StatCard title="Strike Rate" value={`${strikeRate}%`} icon={TrendingUp} color="red" />
                    <StatCard title="Accuracy" value={`${accuracy}%`} icon={BarChart3} color="cyan" />
                    <StatCard title="Fours" value={ballStats.TotalFours} icon={TargetIcon} color="purple" />
                    <StatCard title="Sixes" value={ballStats.TotalSixes} icon={TargetIcon} color="pink" />
                    <StatCard title="Outs" value={ballStats.TotalOut} icon={Activity} color="red" />
                    <StatCard title="Misses" value={ballStats.TotalMiss} icon={Activity} color="gray" />
                </div>

                {/* Overview */}
                <div className="bg-gray-800 p-5 rounded-2xl border border-gray-700">
                    <h3 className="text-white font-bold mb-4 text-lg">Player Overview</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-gray-400 mb-1">Total Visits</p>
                            <p className="text-white font-bold text-xl">{visits.length}</p>
                        </div>
                        <div>
                            <p className="text-gray-400 mb-1">Total Sessions</p>
                            <p className="text-white font-bold text-xl">{filteredSessionsForStats.length}</p>
                        </div>
                        <div>
                            <p className="text-gray-400 mb-1">Most Played Mode</p>
                            <p className="text-white font-bold text-lg">{mostPlayedMode}</p>
                        </div>
                        <div>
                            <p className="text-gray-400 mb-1">Last Activity</p>
                            <p className="text-white font-semibold text-xs">
                                {formatTimestamp(visits[0]?.startTimeStamp, visits[0]?.id)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Recent Sessions */}
                <div className="bg-gray-800 p-5 rounded-2xl border border-gray-700">
                    <h3 className="text-white font-bold mb-4 text-lg">Recent Sessions ({sortedAllSessions.length})</h3>
                    <div className="space-y-2">
                        {sortedAllSessions.slice(0, 10).map(session => (
                            <div key={session.id}>
                                <div 
                                    onClick={() => handleSessionClick(session)}
                                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                        selectedSession?.id === session.id 
                                            ? 'bg-green-900/30 border border-green-500/50' 
                                            : 'bg-gray-700 hover:bg-gray-600'
                                    }`}
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-white font-semibold text-sm">
                                                {formatTimestamp(session.startTimeStamp, session.id)}
                                            </p>
                                            <p className="text-gray-400 text-xs">{session.sessionModeName || 'Unknown'}</p>
                                        </div>
                                        <div className="text-green-400">›</div>
                                    </div>
                                </div>
                                
                                {/* Session Details */}
                                {selectedSession?.id === session.id && (
                                    <div className="mt-2 p-4 bg-gray-700/50 rounded-lg">
                                        {sessionDetails[selectedSession.id]?.loading ? (
                                            <p className="text-center text-green-400">Loading...</p>
                                        ) : sessionDetails[selectedSession.id]?.error ? (
                                            <p className="text-center text-red-400">Error loading details</p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                <DetailItem label="Runs" value={sessionDetails[selectedSession.id]?.totalRuns ?? 'N/A'} />
                                                <DetailItem label="Balls" value={sessionDetails[selectedSession.id]?.totalBallsPlayed ?? 'N/A'} />
                                                <DetailItem label="Strike Rate" value={`${sessionDetails[selectedSession.id]?.strikeRate ?? 'N/A'}%`} />
                                                <DetailItem label="Accuracy" value={`${sessionDetails[selectedSession.id]?.accuracy ?? 'N/A'}%`} />
                                                <DetailItem label="Fours" value={sessionDetails[selectedSession.id]?.totalFour ?? 'N/A'} />
                                                <DetailItem label="Sixes" value={sessionDetails[selectedSession.id]?.totalSixes ?? 'N/A'} />
                                                <DetailItem label="Outs" value={sessionDetails[selectedSession.id]?.totalOut ?? 'N/A'} />
                                                <DetailItem label="Misses" value={sessionDetails[selectedSession.id]?.totalMiss ?? 'N/A'} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                        {sortedAllSessions.length === 0 && (
                            <div className="text-center py-8">
                                {isDataLoading ? (
                                    <div className="flex flex-col items-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500 mb-3"></div>
                                        <p className="text-gray-400 text-sm">Loading sessions...</p>
                                    </div>
                                ) : (
                                    <p className="text-gray-500">No session data available yet.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </div>

            {/* Bottom Navigation */}
            <nav className="bg-black border-t border-gray-800 grid grid-cols-6 pb-safe">
                <TabButton active={false} icon={Home} label="Home" onClick={() => navigate('/')} />
                <TabButton active={false} icon={Camera} label="BioCam" onClick={() => navigate('/')} />
                <TabButton active={false} icon={Calendar} label="Training" onClick={() => navigate('/')} />
                <TabButton active={false} icon={TargetIcon} label="Tactics" onClick={() => navigate('/')} />
                <TabButton active={false} icon={Users} label="Dugout" onClick={() => navigate('/')} />
                <TabButton active={true} icon={BarChart3} label="TrackAI" onClick={() => navigate('/crm')} />
            </nav>
        </div>
    );
}

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color }) => {
    const colorClasses = {
        green: 'bg-green-500/10 border-green-500/30 text-green-400',
        yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
        red: 'bg-red-500/10 border-red-500/30 text-red-400',
        cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
        purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
        pink: 'bg-pink-500/10 border-pink-500/30 text-pink-400',
        gray: 'bg-gray-500/10 border-gray-500/30 text-gray-400',
    };

    return (
        <div className={`${colorClasses[color]} border rounded-xl p-4 flex flex-col items-center justify-center min-h-[100px]`}>
            <Icon size={20} className="mb-2" />
            <span className="text-2xl font-bold">{value}</span>
            <span className="text-xs font-medium mt-1 text-center text-gray-300">{title}</span>
        </div>
    );
};

// Detail Item Component
const DetailItem = ({ label, value }) => (
    <div>
        <span className="text-gray-400 text-xs">{label}:</span>{' '}
        <span className="text-white font-semibold">{value}</span>
    </div>
);

export default PlayerProfile;
